import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { Calendar } from "react-bootstrap-icons";
import { toast } from "react-toastify";

import { upsertCountryScreener } from "../services/APIClient";

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

// Focused single-row editor for one country's screener window,
// as opposed to AddCountryScreenerModal's multi-screener wizard.
const EditCountryScreenerWindowModal = ({
  show,
  onHide,
  country,
  screener,
  screenerName,
  currentUser = "admin",
  onSaved,
}) => {
  const [active, setActive] = useState(false);
  const [opening, setOpening] = useState("");
  const [closing, setClosing] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && screener) {
      setActive(!!screener.active);
      setOpening(toDateInputValue(screener.dateopened));
      setClosing(toDateInputValue(screener.dateclosed));
      setError(null);
    }
  }, [show, screener]);

  const handleSave = async () => {
    // Opening/closing dates are optional - a screener with no dates stays open indefinitely.
    if (opening && closing && closing < opening) {
      setError("The closing date cannot be before the opening date.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        active,
        created_by: screener.created_by || currentUser,
        updated_by: currentUser,
      };
      if (opening) data.dateopened = `${opening}T00:00:00.000Z`;
      if (closing) data.dateclosed = `${closing}T23:59:59.999Z`;

      const updated = await upsertCountryScreener(country.id, screener.assessmentid, data);
      toast.success(`Updated window for ${screenerName || "screener"}`);
      onSaved?.(updated);
    } catch (err) {
      console.error("Error updating screener window:", err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!screener) return null;

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <Calendar className="me-2" />
          Edit Screener Window
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>{screenerName || "Screener"}</strong>
          <div className="text-muted small">{country?.name}</div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Form.Check
          type="switch"
          id="edit-window-active"
          className="mb-3"
          label={active ? "Active" : "Inactive"}
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />

        <Form.Group className="mb-3" controlId="edit-window-opening">
          <Form.Label>Opening date</Form.Label>
          <Form.Control
            type="date"
            value={opening}
            onChange={(event) => setOpening(event.target.value)}
          />
        </Form.Group>

        <Form.Group controlId="edit-window-closing">
          <Form.Label>Closing date</Form.Label>
          <Form.Control
            type="date"
            min={opening || undefined}
            value={closing}
            onChange={(event) => setClosing(event.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-1" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditCountryScreenerWindowModal;
